/**
 * Cliente da API. Tudo passa pelo mesmo site, com o cookie de sessão. O endereço da API segue o
 * endereço base das telas (`APP_PUBLIC_URL`): na raiz é /api; num caminho, /<caminho>/api.
 */

/** Endereço base das telas, sem a barra no fim ('' na raiz, '/meusistema' num caminho). */
export const BASE_PATH = import.meta.env.BASE_URL.replace(/\/+$/, '');
const API_URL = `${BASE_PATH}/api`;

export interface ApiIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly issues: ApiIssue[] = [],
    readonly requestId?: string,
    /** O restante da resposta de erro (ex.: o que impede a escolha de uma proposta). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

/** Idioma da tela (o i18n grava em <html lang>): o servidor responde a validação nele. */
function uiLanguage(): string {
  return document.documentElement.lang || 'pt-BR';
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    signal,
    credentials: 'same-origin',
    headers: {
      'accept-language': uiLanguage(),
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      typeof data.code === 'string' ? data.code : 'internal_error',
      Array.isArray(data.issues) ? (data.issues as ApiIssue[]) : [],
      typeof data.requestId === 'string' ? data.requestId : undefined,
      data,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, options: { signal?: AbortSignal } = {}) =>
    request<T>('GET', path, undefined, options.signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: (path: string) => request<void>('DELETE', path),
};

// ----- Tipos devolvidos pela API -----

export type LocalizedText = { 'pt-BR': string; en: string };

export interface Me {
  user: { id: string; email: string; displayName: string; locale: string; timezone: string };
  /** Entrou com a senha provisória: precisa criar a própria senha antes de usar o sistema. */
  mustChangePassword?: boolean;
  /** Segunda etapa: permissões sensíveis retidas nesta sessão (desligada no login simples). */
  mfa?: { verified: boolean; withheld: string[] };
  /** Como a sessão foi aberta ("amr", ADR 0131). */
  authMethods?: string[];
  /** Permissões que a pessoa tem (valem no sistema todo). */
  permissions: string[];
}

export interface Role {
  id: string;
  code: string;
  name: LocalizedText;
  description: Partial<LocalizedText>;
  isSystem: boolean;
  isActive: boolean;
  permissions: string[];
}

export interface Assignment {
  id: string;
  roleId: string;
  roleCode: string;
  roleName: LocalizedText;
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  locale: 'pt-BR' | 'en';
  timezone: string;
  isActive: boolean;
  linked: boolean;
  lastLoginAt: string | null;
  assignments: Assignment[];
}

export interface PermissionCatalog {
  modules: Record<string, LocalizedText>;
  permissions: { code: string; module: string; label: LocalizedText }[];
}

export interface AuditEntry {
  id: number;
  occurredAt: string;
  actorUserId: string | null;
  tableSchema: string;
  tableName: string;
  recordId: string | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  changedFields: string[] | null;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
}

export interface SecurityEvent {
  id: number;
  occurredAt: string;
  kind: string;
  userId: string | null;
  email: string | null;
  clientIp: string | null;
  details: Record<string, unknown>;
}
